import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const PRESETS = [
  { label: 'Hoy', value: 'today' },
  { label: 'Ayer', value: 'yesterday' },
  { label: 'Últimos 7 días', value: '7d' },
  { label: 'Últimos 15 días', value: '15d' },
  { label: 'Últimos 30 días', value: '30d' },
  { label: 'Últimos 90 días', value: '90d' },
  { label: 'Este mes', value: 'this_month' },
  { label: 'Mes anterior', value: 'last_month' },
];

export function getDateRange(preset) {
  const today = new Date();
  today.setHours(0,0,0,0);
  const fmt = d => d.toISOString().split('T')[0];
  
  switch(preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today), label: 'Hoy' };
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate()-1);
      return { from: fmt(y), to: fmt(y), label: 'Ayer' };
    }
    case '7d': {
      const d = new Date(today); d.setDate(d.getDate()-6);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 7 días' };
    }
    case '15d': {
      const d = new Date(today); d.setDate(d.getDate()-14);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 15 días' };
    }
    case '30d': {
      const d = new Date(today); d.setDate(d.getDate()-29);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 30 días' };
    }
    case '90d': {
      const d = new Date(today); d.setDate(d.getDate()-89);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 90 días' };
    }
    case 'this_month': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(d), to: fmt(today), label: 'Este mes' };
    }
    case 'last_month': {
      const d = new Date(today.getFullYear(), today.getMonth()-1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(d), to: fmt(last), label: 'Mes anterior' };
    }
    default:
      return { from: null, to: null, label: 'Todos' };
  }
}

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAYS = ['Lu','Ma','Mi','Ju','Vi','Sa','Do'];

function MiniCalendar({ year, month, selected, hovering, onSelect, onHover }) {
  const firstDay = new Date(year, month, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) cells.push(i);

  const fmt = (y,m,d) => `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  return (
    <div className="p-3">
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAYS.map(d => <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const date = fmt(year, month, day);
          const isFrom = date === selected?.from;
          const isTo = date === selected?.to;
          const isSelected = isFrom || isTo;
          const inRange = selected?.from && selected?.to && date > selected.from && date < selected.to;
          const inHover = selected?.from && !selected?.to && hovering && date > selected.from && date <= hovering;
          return (
            <button key={i} onClick={() => onSelect(date)} onMouseEnter={() => onHover(date)}
              className={`h-8 w-full text-xs font-medium rounded transition-all
                ${isSelected ? 'bg-indigo-600 text-white' : ''}
                ${inRange || inHover ? 'bg-indigo-100 text-indigo-700' : ''}
                ${!isSelected && !inRange && !inHover ? 'text-gray-700 hover:bg-gray-100' : ''}
              `}>
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DateRangePicker({ value, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState('30d');
  const [selecting, setSelecting] = useState(null);
  const [hovering, setHovering] = useState(null);
  const today = new Date();
  const [leftMonth, setLeftMonth] = useState(today.getMonth() === 0 ? 11 : today.getMonth()-1);
  const [leftYear, setLeftYear] = useState(today.getMonth() === 0 ? today.getFullYear()-1 : today.getFullYear());
  const [rightMonth, setRightMonth] = useState(today.getMonth());
  const [rightYear, setRightYear] = useState(today.getFullYear());
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePreset = (preset) => {
    setActivePreset(preset);
    const range = getDateRange(preset);
    onChange(range);
    setOpen(false);
  };

  const handleDaySelect = (date) => {
    if (!selecting || (selecting.from && selecting.to)) {
      setSelecting({ from: date, to: null });
    } else {
      const from = selecting.from;
      const to = date >= from ? date : from;
      const finalFrom = date >= from ? from : date;
      const range = { from: finalFrom, to, label: `${finalFrom} → ${to}` };
      setSelecting(range);
      onChange(range);
      setActivePreset(null);
    }
  };

  const prevMonths = () => {
    if (leftMonth === 0) { setLeftMonth(11); setLeftYear(y => y-1); setRightMonth(0); setRightYear(rightYear); }
    else { setLeftMonth(m => m-1); setRightMonth(m => m-1); }
  };
  const nextMonths = () => {
    if (rightMonth === 11) { setRightMonth(0); setRightYear(y => y+1); setLeftMonth(11); setLeftYear(leftYear); }
    else { setLeftMonth(m => m+1); setRightMonth(m => m+1); }
  };

  const displayLabel = value?.label || 'Seleccionar período';

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:border-indigo-400 hover:text-indigo-600 transition-all shadow-sm">
        <Calendar className="w-4 h-4" />
        {displayLabel}
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100"
          style={{minWidth: 200}}>
          {/* Presets */}
          <div className="w-44 border-r border-gray-100 p-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">Período</p>
            {PRESETS.map(p => (
              <button key={p.value} onClick={() => handlePreset(p.value)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all mb-0.5
                  ${activePreset === p.value ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>
                {p.label}
              </button>
            ))}
          </div>

          {false && (<div style={{display:'none'}}>
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <button onClick={prevMonths} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
              <div className="flex gap-24">
                <span className="text-sm font-bold text-gray-700">{MONTHS[leftMonth]} {leftYear}</span>
                <span className="text-sm font-bold text-gray-700">{MONTHS[rightMonth]} {rightYear}</span>
              </div>
              <button onClick={nextMonths} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="flex">
              <MiniCalendar year={leftYear} month={leftMonth} selected={selecting || value}
                hovering={hovering} onSelect={handleDaySelect} onHover={setHovering} />
              <div className="w-px bg-gray-100 my-3" />
              <MiniCalendar year={rightYear} month={rightMonth} selected={selecting || value}
                hovering={hovering} onSelect={handleDaySelect} onHover={setHovering} />
            </div>
            {selecting?.from && !selecting?.to && (
              <p className="text-xs text-center text-gray-400 pb-3">Selecciona la fecha final</p>
            )}
            {selecting?.from && selecting?.to && (
              <div className="flex items-center justify-between px-4 pb-4">
                <span className="text-xs text-gray-500">{selecting.from} → {selecting.to}</span>
                <button onClick={() => { onChange(selecting); setOpen(false); }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700">
                  Aplicar
                </button>
              </div>
            )}
          </div>)}
        </div>
      )}
    </div>
  );
}
